<script lang="ts">
  import { Activity, AlertCircle, Loader2, WifiOff } from 'lucide-svelte';
  import { livePriceStore } from '$lib/stores/livePrice.svelte';
  import { Tooltip, TooltipContent, TooltipTrigger } from '$lib/components/ui/tooltip';

  // ============================================================
  // <LivePriceTicker>
  //
  // Muestra los precios actuales de cada simbolo activo (open positions de
  // Live/Developer Monitor) en el topbar, al lado del clock. Sirve como:
  //  - Indicador visual de health del WS proxy (verde si recibe candles).
  //  - Visibilidad permanente de los precios sin abrir el tab Live Monitor.
  //
  // Si no hay simbolos activos, muestra solo un dot gris discreto + tooltip
  // explicando que no hay open positions.
  // ============================================================

  // Precios y status del store (reactivos).
  let prices = $derived([...livePriceStore.prices.values()].sort((a, b) =>
    a.symbol.localeCompare(b.symbol),
  ));
  let status = $derived(livePriceStore.status);
  let upstreamStatus = $derived(livePriceStore.upstreamStatus);
  let lastMessage = $derived(livePriceStore.lastMessage);

  // Color del dot segun health combinado.
  let dotClass = $derived.by((): string => {
    if (status !== 'open') return 'bg-muted-foreground/40'; // gris
    if (upstreamStatus !== 'open') return 'bg-warning'; // proxy ok, upstream no
    return 'bg-success animate-pulse'; // todo verde
  });

  let connectionLabel = $derived.by((): string => {
    if (status === 'idle') return 'Idle (no symbols)';
    if (status === 'connecting') return 'Connecting…';
    if (status === 'reconnecting') return 'Reconnecting…';
    if (status === 'closed') return 'Disconnected';
    // status === 'open'
    if (upstreamStatus === 'open') return 'Connected · receiving candles';
    if (upstreamStatus === 'connecting' || upstreamStatus === 'reconnecting') {
      return 'Proxy OK, upstream reconnecting';
    }
    return `Proxy OK, upstream ${upstreamStatus}`;
  });

  function fmtPrice(n: number): string {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function fmtLastMessage(d: Date | null): string {
    if (!d) return 'never';
    const diffMs = Date.now() - d.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
</script>

<Tooltip>
  <TooltipTrigger>
    {#snippet child({ props })}
      <button
        {...props}
        type="button"
        class={[
          'hidden items-center gap-2 rounded-md border px-2.5 py-1 text-xs transition-colors sm:inline-flex',
          // Mismo lenguaje visual que los chips del Aggregate Performance bar:
          // borde + bg traslucido + texto primary. Cambia con la palette
          // (rabbit naranja / classic verde).
          prices.length === 0
            ? 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50'
            : 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15',
        ].join(' ')}
        aria-label="Live price status"
      >
        <!-- Dot indicador -->
        <span
          class={['size-2 shrink-0 rounded-full', dotClass].join(' ')}
          aria-hidden="true"
        ></span>

        {#if prices.length === 0}
          <!-- Sin simbolos activos -->
          <span class="inline-flex items-center gap-1">
            {#if status === 'connecting' || status === 'reconnecting'}
              <Loader2 class="size-3 animate-spin" />
            {:else if status === 'closed'}
              <WifiOff class="size-3" />
            {:else}
              <Activity class="size-3" />
            {/if}
            <span class="tracking-wide">no symbols</span>
          </span>
        {:else}
          <!-- Lista compacta de precios con jerarquia: symbol semi-bold,
               precio bold tabular-nums, separadores discretos. -->
          <div class="flex items-center gap-2 font-mono tabular-nums">
            {#each prices as p, i (p.symbol)}
              {#if i > 0}
                <span class="text-primary/30">·</span>
              {/if}
              <span class="inline-flex items-baseline gap-1">
                <span class="font-semibold tracking-wide">{p.symbol}</span>
                <span class="text-foreground font-bold">{fmtPrice(p.price)}</span>
              </span>
            {/each}
          </div>
        {/if}
      </button>
    {/snippet}
  </TooltipTrigger>
  <TooltipContent class="font-sans">
    <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      <span class="text-muted-foreground">Proxy</span>
      <span class="font-medium">{connectionLabel}</span>
      <span class="text-muted-foreground">Upstream</span>
      <span class="font-medium font-mono">{upstreamStatus}</span>
      <span class="text-muted-foreground">Last candle</span>
      <span class="font-medium font-mono">{fmtLastMessage(lastMessage)}</span>
      <span class="text-muted-foreground">Symbols</span>
      <span class="font-medium font-mono">
        {prices.length === 0 ? 'none' : prices.map((p) => p.symbol).join(', ')}
      </span>
    </div>
    {#if status !== 'open'}
      <div class="text-warning mt-2 inline-flex items-center gap-1 text-[10px]">
        <AlertCircle class="size-3" />
        WebSocket not connected
      </div>
    {/if}
  </TooltipContent>
</Tooltip>
